import { z } from "zod";

/**
 * The organization roles, defined once. `apps/backend/src/auth/access.ts`
 * builds its access-control roles from these names and the front ends gate
 * their UI on them, so a typo cannot make the two disagree.
 */
export const RoleSchema = z.enum(["tenant_admin", "manager", "cashier", "kitchen"]);
export type Role = z.infer<typeof RoleSchema>;

export const OrganizationPlanSchema = z.enum(["free", "pro", "enterprise"]);
export type OrganizationPlan = z.infer<typeof OrganizationPlanSchema>;

export const OrganizationStatusSchema = z.enum(["active", "suspended", "archived"]);
export type OrganizationStatus = z.infer<typeof OrganizationStatusSchema>;

export const LandingModeSchema = z.enum(["shared", "custom"]);
export type LandingMode = z.infer<typeof LandingModeSchema>;

/**
 * The store, as Better Auth's organization plugin owns it.
 *
 * There is no `theme` here any more. It was a nullable free-form `text`
 * column that nothing ever wrote and nothing typed. A store's presentation now
 * lives on `store_profile` as a validated `ThemeSchema` (see
 * `entities/theme.ts`) — a second, untyped home for the same idea would only
 * invite the two to disagree about which one a page should read.
 */
export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(128),
  slug: z.string().min(2).max(64).regex(/^[a-z0-9-]+$/),
  plan: OrganizationPlanSchema,
  status: OrganizationStatusSchema,
  landingMode: LandingModeSchema,
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const MemberSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  role: RoleSchema,
});
export type Member = z.infer<typeof MemberSchema>;
